import { evaluateExpression } from '@framework/engine/schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '@framework/engine/schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '@framework/engine/schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '@framework/engine/schema/expressions/references/collectExpressionReferences.ts';
import { type IParentWidgetScope } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { setComputedBooleanTransform } from '@framework/engine/schema/widgets/repository/computed/boolean/setComputedBooleanTransform.ts';
import { getWidgetName } from '@framework/engine/validator/form-builder/getWidgetName.ts';
import { yup } from '@framework/validation/initYup.ts';
import { type IModuleTriggerWidget } from './IModuleTriggerWidget.ts';
import { TriggerModuleMetaKey } from './ModuleTriggerWidget.type.tsx';

export const ModuleTriggerModuleUniqueCodeMetaKey =
  'ModuleTriggerModuleUniqueCode';

export const ModuleTriggerTitleMetaKey = 'ModuleTriggerTitle';

export interface ITriggeredModuleMeta {
  moduleId: string | null | undefined;
}

export function getModuleTriggerUniqueCodeName(widget: IModuleTriggerWidget) {
  return `${getWidgetName(widget)}@internal_uniqueModuleCode`;
}

export function createModuleTriggerFormSchema(input: {
  widget: IModuleTriggerWidget;
  parentScope: IParentWidgetScope;
}) {
  const { widget, parentScope } = input;

  // Nel caso in cui il widget non venga configurato correttamente,
  // non viene restituito alcuno schema, per evitare che la validazione
  // vada in errore.
  if (!widget.moduleId) {
    return {};
  }

  const name = getWidgetName(widget);

  const widgetSchema = createModuleTriggerSchema(widget, parentScope);

  return {
    [name]: widgetSchema,
  };
}

export function createModuleTriggerSchema(
  widget: Omit<IModuleTriggerWidget, 'type'>,
  parentScope: IParentWidgetScope,
) {
  const meta: ITriggeredModuleMeta | null = { moduleId: widget.moduleId };

  let widgetSchema = createTriggerableSchema(widget, parentScope, meta);
  widgetSchema = createModuleTriggerUniqueCodeSchema(widget, widgetSchema);
  widgetSchema = createModuleTriggerTitleSchema(widget, widgetSchema);
  return widgetSchema;
}

export function createModuleTriggerTitleSchema(
  widget: Pick<IModuleTriggerWidget, 'title'>,
  schema: yup.AnySchema,
) {
  if (widget.title) {
    // Risoluzione campo titolo
    const titleExpression = widget.title;
    const titleReferences = collectExpressionReferences(titleExpression);
    const titlePaths = titleReferences.map(r => mapReferenceToPath(r));

    const resultSchema = schema.parentWhen(titlePaths, (values, schema) => {
      const variables = createEvaluationVariables(titleReferences, values);
      const result = evaluateExpression(titleExpression, { variables });

      return schema.meta({ [ModuleTriggerTitleMetaKey]: result });
    });
    return resultSchema;
  }

  return schema;
}

export function createModuleTriggerUniqueCodeSchema(
  widget: Pick<
    IModuleTriggerWidget,
    'needUniqueModuleCode' | 'uniqueModuleCode'
  >,
  schema: yup.AnySchema,
) {
  if (widget.needUniqueModuleCode && widget.uniqueModuleCode) {
    // Se è indicato un codice univoco modulo, lo risolvo in fase di validazione
    // (il campo è un'expression)
    const codeExpression = widget.uniqueModuleCode;
    const codeReferences = collectExpressionReferences(codeExpression);
    const codePaths = codeReferences.map(r => mapReferenceToPath(r));
    const widgetSchema = schema.parentWhen(
      codePaths,
      (values, schema: yup.AnySchema, context: any) => {
        const variables = createEvaluationVariables(codeReferences, values);
        const result = evaluateExpression(codeExpression, { variables });

        return schema.meta({
          [ModuleTriggerModuleUniqueCodeMetaKey]: result,
        });
      },
    );
    return widgetSchema;
  }

  return schema;
}

export function createTriggerableSchema(
  widget: Pick<IModuleTriggerWidget, 'expression'>,
  parentScope: IParentWidgetScope,
  meta: Record<string, any> | null | undefined,
) {
  return setComputedBooleanTransform(
    widget,
    parentScope,
    yup
      .boolean()
      .notRequired()
      .meta({
        [TriggerModuleMetaKey]: meta,
      }),
  );
}
